// Module 10 — Renewals.
// Route: /reinsurance/renewals
//
// Upcoming treaty and facultative renewals. The statuses are an urgency
// vocabulary — On Track / Due Soon / At Risk / Overdue — because a renewal
// pipeline is read for what needs attention today, not for which procedural
// step a file last passed.

import React from 'react';
import { useTranslation } from 'react-i18next';
import ModulePage from './ModulePage';
import RenewalDrawer from './RenewalDrawer';
import {
  C, Icons, Kpi, Pill, PrimaryButton, GhostButton, date, humanise,
  BASE_URL,
} from './kit';

const renewalForm = (t) => ({
  title: t('screens.reRenewals.newTitle', 'New renewal plan'),
  subtitle: t('screens.reRenewals.newSub', 'Plan an expiring programme back into the market'),
  submitLabel: t('screens.reRenewals.createBtn', 'Create plan'),
  endpoint: '/reinsurance/renewals',
  fields: [
    { key: 'cedantName', label: t('screens.reRenewals.fCedantIn', 'Cedant / client'), required: true,
      placeholder: 'Emirates Energy PJSC' },
    { key: 'classOfBusiness', label: t('screens.reRenewals.fClassIn', 'Class'), required: true,
      placeholder: 'Energy' },
    { key: 'type', label: t('screens.reRenewals.fTypeIn', 'Type'), type: 'select', required: true,
      defaultValue: 'treaty',
      options: [
        { value: 'treaty', label: 'Treaty' },
        { value: 'facultative', label: 'Facultative' },
      ] },
    // The schema requires the treaty itself, not its number — see the
    // beforeCreate note in reinsuranceRenewalController.js.
    { key: 'treaty', label: t('screens.reRenewals.fTreatyIn', 'Expiring treaty'),
      type: 'lookup', required: true, endpoint: '/reinsurance/treaties',
      optionLabel: (r) => [r.treatyNumber, r.name || r.cedantName].filter(Boolean).join(' — '),
      placeholder: t('screens.reRenewals.fTreatyPick', 'Choose the expiring treaty…'),
      emptyLabel: t('screens.reRenewals.fTreatyNone', 'No treaties yet'),
      emptyHelp: t('screens.reRenewals.fTreatyNoneHelp', 'Create a treaty first — a renewal plan renews an existing programme.') },
    { key: 'renewalDate', label: t('screens.reRenewals.fDateIn', 'Renewal date'), type: 'date', required: true },
    { key: 'targetStrategy', label: t('screens.reRenewals.fStrategyIn', 'Target strategy'),
      placeholder: 'Maintain terms' },
    { key: 'handlerName', label: t('screens.reRenewals.fHandlerIn', 'Handler'),
      placeholder: 'Hisham Al Hashimi' },
    { key: 'expiringPremium', label: t('screens.reRenewals.fPremiumIn', 'Expiring premium'), type: 'number',
      placeholder: '21000000' },
    { key: 'currency', label: t('screens.reRenewals.fCurrencyIn', 'Currency'), defaultValue: 'USD' },
  ],
  toPayload: (v) => ({
    cedantName: v.cedantName.trim(),
    classOfBusiness: v.classOfBusiness.trim(),
    type: v.type,
    // The lookup yields the treaty's id; beforeCreate denormalises its
    // number and name server-side. Sending treatyNumber here would drop the
    // required ref and reinstate the 'Path `treaty` is required' failure.
    ...(v.treaty ? { treaty: v.treaty } : {}),
    renewalDate: v.renewalDate,
    ...(v.targetStrategy ? { targetStrategy: v.targetStrategy.trim() } : {}),
    ...(v.handlerName ? { handlerName: v.handlerName.trim() } : {}),
    ...(v.expiringPremium !== '' ? { premium: { expiring: Number(v.expiringPremium) } } : {}),
    currency: (v.currency || 'USD').trim().toUpperCase(),
    status: 'on_track',
    // The standard renewal run, so the checklist is something to work rather
    // than something to build before you can start working.
    checklist: [
      'Review expiring terms and conditions',
      'Prepare renewal strategy',
      'Update exposure data',
      'Send renewal pack to markets',
      'Follow up with brokers',
      'Finalize terms and obtain indications',
    ].map((label, i) => ({ key: `step${i + 1}`, label, completed: false })),
  }),
});

export default function RenewalsList() {
  const { t } = useTranslation();

  const columns = [
    { key: 'renewalNumber', label: t('screens.reRenewals.colNumber', 'Renewal ref'), width: 142,
      render: (r) => <span style={{ fontWeight: 600, color: C.ink }}>{r.renewalNumber}</span> },
    { key: 'cedantName', label: t('screens.reRenewals.colCedant', 'Cedant / client'),
      render: (r) => r.cedantName || r.treatyName || '—' },
    { key: 'classOfBusiness', label: t('screens.reRenewals.colClass', 'Class'), width: 108,
      render: (r) => (r.classOfBusiness
        ? <Pill status='draft' label={r.classOfBusiness} size='sm' />
        : <span style={{ color: C.faint }}>—</span>) },
    { key: 'type', label: t('screens.reRenewals.colType', 'Type'), width: 104,
      render: (r) => <Pill status={r.type === 'facultative' ? 'quoted' : 'in_review'}
        label={humanise(r.type || 'treaty')} size='sm' /> },
    { key: 'renewalDate', label: t('screens.reRenewals.colDate', 'Renewal date'), width: 116,
      render: (r) => date(r.renewalDate) },
    { key: 'currentMarkets', label: t('screens.reRenewals.colMarkets', 'Current markets'),
      render: (r) => {
        const m = r.currentMarkets?.length ? r.currentMarkets
          : (r.expiringTerms?.shares || []).map(s => s.name).filter(Boolean);
        if (!m.length) return <span style={{ color: C.faint }}>—</span>;
        return <span style={{ fontSize: 12.5 }}>{m.slice(0, 2).join(', ')}{m.length > 2 ? ` +${m.length - 2}` : ''}</span>;
      } },
    { key: 'targetStrategy', label: t('screens.reRenewals.colStrategy', 'Target strategy'),
      render: (r) => r.targetStrategy || <span style={{ color: C.faint }}>—</span> },
    { key: 'handlerName', label: t('screens.reRenewals.colHandler', 'Handler'),
      render: (r) => r.handlerName || <span style={{ color: C.faint }}>unassigned</span> },
    { key: 'status', label: t('screens.reRenewals.colStatus', 'Status'), width: 100,
      render: (r) => <Pill status={r.status} /> },
  ];

  return (
    <ModulePage
      title={t('screens.reRenewals.title', 'Renewals')}
      subtitle={t('screens.reRenewals.subtitle', 'Upcoming treaty and facultative renewals')}
      endpoint='/reinsurance/renewals'
      searchPlaceholder={t('screens.reRenewals.search', 'Search renewals, cedants, or reference…')}
      createForm={renewalForm(t)}
      actions={({ openCreate }) => (
        <>
          {/* A renewal desk works to dates that live in Outlook. The feed
              carries each renewal date plus every key date still outstanding,
              which are the ones people actually miss. */}
          <GhostButton
            icon={Icons.cal()}
            onClick={() => window.open(`${BASE_URL}/reinsurance/renewals/calendar.ics`, '_blank', 'noopener')}
          >
            {t('screens.reRenewals.export', 'Export calendar')}
          </GhostButton>
          <PrimaryButton onClick={openCreate}>{t('screens.reRenewals.create', 'Create renewal plan')}</PrimaryButton>
        </>
      )}
      renderKpis={(s) => (
        <>
          <Kpi icon={Icons.cal()} tone='blue'
            value={s?.dueIn30Days?.value ?? '—'} trend={s?.dueIn30Days?.trend}
            comparison={s?.dueIn30Days?.comparison}
            label={t('screens.reRenewals.kpiDue', 'Renewals due in 30 days')} />
          <Kpi icon={Icons.users()} tone='green'
            value={s?.brokerActions?.value ?? '—'} trend={s?.brokerActions?.trend}
            comparison={s?.brokerActions?.comparison}
            label={t('screens.reRenewals.kpiActions', 'Broker actions pending')} />
          <Kpi icon={Icons.doc()} tone='amber'
            value={s?.slipUpdates?.value ?? '—'} trend={s?.slipUpdates?.trend}
            comparison={s?.slipUpdates?.comparison}
            label={t('screens.reRenewals.kpiSlips', 'Slip updates required')} />
          <Kpi icon={Icons.alert()} tone='red'
            value={s?.atRisk?.value ?? '—'} trend={s?.atRisk?.trend}
            comparison={s?.atRisk?.comparison}
            label={t('screens.reRenewals.kpiRisk', 'At-risk accounts')} />
        </>
      )}
      filters={[
        { key: 'period', type: 'daterange', label: 'Renewing' },
        { key: 'status', label: 'Status', options: [
          { value: 'all', label: 'All statuses' },
          { value: 'on_track', label: 'On Track' },
          { value: 'due_soon', label: 'Due Soon' },
          { value: 'at_risk', label: 'At Risk' },
          { value: 'overdue', label: 'Overdue' },
          { value: 'renewed', label: 'Renewed' },
          { value: 'lapsed', label: 'Lapsed' },
          { value: 'declined', label: 'Declined' },
        ] },
        { key: 'type', label: 'Type', options: [
          { value: 'all', label: 'All types' },
          { value: 'treaty', label: 'Treaty' },
          { value: 'facultative', label: 'Facultative' },
        ] },
      ]}
      columns={columns}
      emptyMessage={t('screens.reRenewals.empty', 'No renewals open. Generate them from treaties expiring in a window.')}
      drawerTabs={['Overview', 'Timeline', 'Documents', 'Markets']}
      drawerHeader={(r) => ({
        title: r.renewalNumber,
        badge: <Pill status={r.status} />,
        lines: [
          r.cedantName || r.treatyName,
          [r.classOfBusiness, humanise(r.type || 'treaty'), `Renewal: ${date(r.renewalDate)}`].filter(Boolean).join(' · '),
        ].filter(Boolean),
      })}
      drawer={(r, { tab, reload }) => (
        <RenewalDrawer row={r} tab={tab} reload={reload} />
      )}
    />
  );
}
